import { decodePathSegment } from "./rail-sections";

// Siderail density is a per-user, per-browser preference. The key carries the
// AlwaysAI user id so a shared machine never replays one person's layout for
// the next sign-in; the stored value is only ever "0"/"1" (never PHI).
export function siderailCollapsedStorageKey(userId: string): string {
  // Still `rail-collapsed`: renaming a persisted key would reset every user's
  // density preference to expanded.
  return `alwaysai:rail-collapsed:${userId}`;
}

export function parseStoredSiderailCollapsed(stored: string | null): boolean {
  return stored === "1";
}

export function serializeSiderailCollapsed(collapsed: boolean): string {
  return collapsed ? "1" : "0";
}

// The compact tooltip carries the name in full: it is the only place the
// attribution is legible once the labels are clipped.
export function appointmentsTooltipLabel(
  viewingOther: boolean,
  viewedRdName: string | null
): string {
  if (!viewingOther) return "My Appointments";

  return viewedRdName ? `Appointments — ${viewedRdName}` : "Appointments";
}

// A malformed escape (`/appointments/%zz`) decodes to null: no Appointment is
// open, rather than a URIError thrown out of the rail.
export function activeAppointmentIdFor(pathname: string): string | null {
  return pathname.startsWith("/appointments/")
    ? decodePathSegment(pathname.slice("/appointments/".length))
    : null;
}

// The Today panel mounts only while actually visible: its PHI-bearing
// query must not keep refetching behind a thin siderail or a closed drawer.
export function isTodayPanelVisible({
  scheduleOpen,
  collapsed,
  isDesktop,
  drawerOpen,
}: {
  scheduleOpen: boolean;
  collapsed: boolean;
  isDesktop: boolean;
  drawerOpen: boolean;
}): boolean {
  const thinDesktopRail = isDesktop && collapsed;
  const siderailOnScreen = isDesktop || drawerOpen;

  return scheduleOpen && !thinDesktopRail && siderailOnScreen;
}

// A "+" marks a remaining count that may continue past the first page.
export function todayCountLabel(remaining: number, todayCount: number | null): string {
  if (remaining === 0) return "";

  return todayCount !== null ? `${remaining}` : `${remaining}+`;
}

/**
 * A choice that lasts for one visit to a page: the pathname it was made on
 * and its value (null before any choice).
 */
export interface PageVisit<T> {
  path: string;
  value: T | null;
}

/**
 * The visit as of `pathname`. Any change of pathname starts a fresh visit, so
 * a choice made on A is gone on returning to A by way of B.
 */
export function pageVisitAt<T>(visit: PageVisit<T>, pathname: string): PageVisit<T> {
  return visit.path === pathname ? visit : { path: pathname, value: null };
}

// The single rail (NO-754) folds to its icon strip on arrival at a working
// surface (an open Appointment, a SOAP Draft) without touching the stored
// density. Expanding it there lasts for this visit only; the shell starts a
// fresh visit on every pathname change (`pageVisitAt`), including a return to
// the same page. A workspace drawer's squeeze still applies on top.
export function railCollapsed({
  stored,
  folded,
  expandedThisVisit,
  squeezed,
  isSqueezeWidth,
}: {
  stored: boolean;
  folded: boolean;
  expandedThisVisit: boolean;
  squeezed: boolean;
  isSqueezeWidth: boolean;
}): boolean {
  const chosen = folded ? !expandedThisVisit : stored;

  return chosen || (squeezed && isSqueezeWidth);
}

export type RailToggleIntent =
  | "expand-visit"
  | "collapse-visit"
  | "store-collapsed"
  | "store-expanded";

// On a fold path the toggle changes this visit only, never the stored
// density; elsewhere it stores the opposite of what is rendered, as the RD
// shell does.
export function railToggleIntent({
  folded,
  collapsed,
}: {
  folded: boolean;
  collapsed: boolean;
}): RailToggleIntent {
  if (folded) return collapsed ? "expand-visit" : "collapse-visit";

  return collapsed ? "store-expanded" : "store-collapsed";
}
